import React from "react"
import {t, msg} from "@lingui/core/macro"
import {Trans} from "@lingui/react/macro"
import {i18n} from "@lingui/core"
import {API_URL} from "../../../config"
import {getTravelText, isTravelTokenHidden} from "../../../components/travelTextTokens"

const STORAGE_KEY = "travel-chat-session"
const TEASER_DISMISSED_KEY = "travel-chat-teaser-dismissed"
const CHAT_OPENED_KEY = "travel-chat-opened-once"
const ENDPOINT = API_URL + "/travel-chat"

// Phrases d'incitation affichées en rotation dans le teaser. Volontairement
// courtes et orientées tourisme. La rotation évite la lassitude visuelle si
// l'utilisateur reste longtemps sur le site et voit le teaser plusieurs fois.
const TEASER_MESSAGES = [
    {key: "chat_teaser_general", emoji: "👋", text: msg`Une question ? Je suis là pour vous aider !`},
    {key: "chat_teaser_kosher", emoji: "🍽️", text: msg`Besoin d'un resto casher ? Demandez-moi !`},
    {key: "chat_teaser_hotels", emoji: "🏨", text: msg`À la recherche d'un hôtel ? Je peux vous orienter.`},
    {key: "chat_teaser_shabbat", emoji: "🕯️", text: msg`Programme de Chabbat, horaires, zmanim — n'hésitez pas !`},
    {key: "chat_teaser_stay", emoji: "💬", text: msg`Posez-moi vos questions sur votre séjour.`},
]

// Première apparition rapide pour capter l'attention dès l'arrivée, puis
// répétitions plus espacées pour ne pas être intrusif.
const TEASER_FIRST_DELAY_MS = 2500
const TEASER_VISIBLE_MS = 10000
const TEASER_REPEAT_INTERVAL_MS = 70000


function readSessionFromStorage() {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY)
        if (!raw) return null
        return JSON.parse(raw)
    } catch (_) {
        return null
    }
}


function writeSessionToStorage(data) {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    } catch (_) {
    }
}


function clearSessionStorage() {
    try {
        sessionStorage.removeItem(STORAGE_KEY)
    } catch (_) {
    }
}


function siteIdentifierFromOrg(org) {
    if (!org) return null
    return org.prefix || org.domain || null
}


// Parse une frame SSE ADK ("data: {...}") en objet JS. Les frames ADK
// contiennent un seul `data:` par event mais on supporte le multi-ligne
// par sécurité (concat selon spec SSE). Retourne null si vide ou non-JSON.
function parseAdkSseFrame(frame) {
    const lines = frame.split("\n")
    let payload = ""
    for (const line of lines) {
        if (line.startsWith("data:")) {
            payload += line.slice(5).replace(/^\s/, "")
        }
    }
    if (!payload) return null
    try {
        return JSON.parse(payload)
    } catch (_) {
        return null
    }
}


// Mini-renderer markdown safe et sans dépendances. Couvre les cas réellement
// produits par l'agent : **gras**, *italique*, `code`, listes -/*/1., liens
// [texte](url), citations >, blocs de code ```...```. Tout ce qui n'est pas
// reconnu reste affiché en texte brut, et les contenus sont systématiquement
// échappés avant transformation pour éviter toute injection HTML.

function escapeHtml(s) {
    return String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;")
}


function renderInline(s) {
    // Code inline d'abord (placeholder pour ne pas être touché par les autres regex).
    const codes = []
    let out = s.replace(/`([^`]+)`/g, (_, code) => {
        codes.push(escapeHtml(code))
        return ` CODE${codes.length - 1} `
    })
    out = escapeHtml(out)
    // Liens
    out = out.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+|tel:[^\s)]+|mailto:[^\s)]+)\)/g,
        (_, label, url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`)
    // Auto-lien URL nues
    out = out.replace(/(^|[\s(])((https?:\/\/[^\s<]+))/g,
        (_, pre, url) => `${pre}<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`)
    // Gras puis italique
    out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    out = out.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, "$1<em>$2</em>")
    // Restaurer code inline
    out = out.replace(/ CODE(\d+) /g, (_, i) => `<code>${codes[Number(i)]}</code>`)
    return out
}


function renderMarkdown(text) {
    if (!text) return ""
    const lines = String(text).replace(/\r\n/g, "\n").split("\n")
    const out = []
    let inList = null  // "ul" | "ol" | null
    let inQuote = false
    let inCode = false
    let codeBuffer = []

    function closeList() {
        if (inList) {
            out.push(`</${inList}>`)
            inList = null
        }
    }

    function closeQuote() {
        if (inQuote) {
            out.push("</blockquote>")
            inQuote = false
        }
    }

    for (const raw of lines) {
        const line = raw

        // Bloc de code
        if (/^```/.test(line)) {
            if (inCode) {
                out.push(`<pre><code>${escapeHtml(codeBuffer.join("\n"))}</code></pre>`)
                codeBuffer = []
                inCode = false
            } else {
                closeList()
                closeQuote()
                inCode = true
            }
            continue
        }
        if (inCode) {
            codeBuffer.push(line)
            continue
        }

        // Liste à puces
        const ulMatch = line.match(/^[\s]*[-*]\s+(.+)$/)
        if (ulMatch) {
            closeQuote()
            if (inList !== "ul") {
                closeList()
                out.push("<ul>")
                inList = "ul"
            }
            out.push(`<li>${renderInline(ulMatch[1])}</li>`)
            continue
        }

        // Liste numérotée
        const olMatch = line.match(/^[\s]*\d+\.\s+(.+)$/)
        if (olMatch) {
            closeQuote()
            if (inList !== "ol") {
                closeList()
                out.push("<ol>")
                inList = "ol"
            }
            out.push(`<li>${renderInline(olMatch[1])}</li>`)
            continue
        }

        // Citation
        const quoteMatch = line.match(/^>\s?(.*)$/)
        if (quoteMatch) {
            closeList()
            if (!inQuote) {
                out.push("<blockquote>")
                inQuote = true
            }
            out.push(renderInline(quoteMatch[1]))
            out.push("<br/>")
            continue
        }

        // Ligne vide → ferme tout
        if (line.trim() === "") {
            closeList()
            closeQuote()
            out.push("")
            continue
        }

        // Paragraphe simple
        closeList()
        closeQuote()
        out.push(`<p>${renderInline(line)}</p>`)
    }

    if (inCode && codeBuffer.length) {
        out.push(`<pre><code>${escapeHtml(codeBuffer.join("\n"))}</code></pre>`)
    }
    closeList()
    closeQuote()

    return out.join("\n")
}


function MessageList({messages, pending}) {
    const ref = React.useRef(null)

    React.useEffect(() => {
        if (ref.current) {
            ref.current.scrollTop = ref.current.scrollHeight
        }
    }, [messages.length, pending])

    return (
        <div ref={ref} className="trchat__messages">
            <div className="trchat__system-note">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="16" x2="12" y2="12"/>
                    <line x1="12" y1="8" x2="12.01" y2="8"/>
                </svg>
                <span>
                    <Trans>Assistant virtuel — peut faire des erreurs. Pour toute information importante, contactez directement l'équipe.</Trans>
                </span>
            </div>
            {messages.map((m, i) => (
                <div key={m.id || i} className={`trchat__msg trchat__msg--${m.role}`}>
                    {m.role === "assistant" ? (
                        m.thinking && !m.text ? (
                            <div className="trchat__bubble trchat__bubble--thinking">
                                <span className="trchat__thinking-dots">
                                    <span></span><span></span><span></span>
                                </span>
                                <span className="trchat__thinking-label"><Trans>Réflexion en cours…</Trans></span>
                            </div>
                        ) : (
                            <div
                                className="trchat__bubble trchat__bubble--md"
                                dangerouslySetInnerHTML={{__html: renderMarkdown(m.text)}}
                            />
                        )
                    ) : (
                        <div className="trchat__bubble">{m.text}</div>
                    )}
                </div>
            ))}
            {pending && (
                <div className="trchat__msg trchat__msg--assistant">
                    <div className="trchat__bubble trchat__bubble--typing">
                        <span></span><span></span><span></span>
                    </div>
                </div>
            )}
        </div>
    )
}


function ChatPanel({open, closing, isMobile, messages, pending, error, onSend, onClose, onReset, headerLabel}) {
    const [draft, setDraft] = React.useState("")
    const inputRef = React.useRef(null)

    React.useEffect(() => {
        if (open && !isMobile && inputRef.current) {
            // Auto-focus desktop seulement (mobile : éviter clavier intempestif)
            inputRef.current.focus()
        }
    }, [open, isMobile])

    function submit(e) {
        if (e) e.preventDefault()
        const text = draft.trim()
        if (!text || pending) return
        setDraft("")
        onSend(text)
    }

    function onKeyDown(e) {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault()
            submit()
        }
    }

    if (!open && !closing) return null

    return (
        <div className={`trchat__panel ${closing ? "trchat__panel--closing" : ""} ${isMobile ? "trchat__panel--mobile" : ""}`}>
            <div className="trchat__header">
                <div className="trchat__header-text">
                    <div className="trchat__header-title">{headerLabel}</div>
                    <div className="trchat__header-status">
                        <span className="trchat__dot"/> <Trans>En ligne</Trans>
                    </div>
                </div>
                <div className="trchat__header-actions">
                    <button
                        type="button"
                        className="trchat__icon-btn"
                        aria-label={t`Nouvelle conversation`}
                        title={t`Nouvelle conversation`}
                        onClick={onReset}
                        disabled={pending}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 12a9 9 0 1 0 3-6.7"/>
                            <polyline points="3 4 3 10 9 10"/>
                        </svg>
                    </button>
                    <button type="button" className="trchat__icon-btn" aria-label={t`Fermer`} onClick={onClose}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>
            </div>

            <MessageList messages={messages} pending={pending}/>

            {error && (
                <div className="trchat__error">{error}</div>
            )}

            <form className="trchat__input-row" onSubmit={submit}>
                <textarea
                    ref={inputRef}
                    className="trchat__input"
                    placeholder={t`Écrire un message…`}
                    rows={1}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onKeyDown}
                    disabled={pending}
                />
                <button
                    type="submit"
                    className="trchat__send"
                    disabled={!draft.trim() || pending}
                    aria-label={t`Envoyer`}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13"/>
                        <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                    </svg>
                </button>
            </form>
        </div>
    )
}


export default function TravelChatbot({context}) {
    const chatbot = context?.travel?.chatbot || {}
    const enabled = chatbot.enabled === true
    const welcomeMessage = chatbot.welcomeMessage || ""

    const isMobile = !!context?.isMobile
    const language = context?.lang?.value || context?.org?.language || "fr"
    const siteIdentifier = siteIdentifierFromOrg(context?.org)
    const teaserMessages = React.useMemo(() => TEASER_MESSAGES
        .filter(item => !isTravelTokenHidden(context?.travel, item.key))
        .map(item => ({...item, text: getTravelText(context?.travel, item.key, i18n._(item.text), language)})),
        [context?.travel, language])
    // Couleur primaire de l'org : --primary est défini sur .travel-page mais le
    // chatbot est rendu hors de ce wrapper (FAB position:fixed sur <body>). On
    // récupère donc la couleur via la config travel et on la pose en custom
    // property locale au scope de notre wrapper racine.
    const primaryColor = context?.travel?.theme?.primaryColor || "#1b579e"

    const [open, setOpen] = React.useState(false)
    const [closing, setClosing] = React.useState(false)
    const [messages, setMessages] = React.useState([])
    const [pending, setPending] = React.useState(false)
    const [error, setError] = React.useState(null)
    const [sessionToken, setSessionToken] = React.useState(null)
    const [teaserVisible, setTeaserVisible] = React.useState(false)
    const [teaserIndex, setTeaserIndex] = React.useState(0)
    const [openedOnce, setOpenedOnce] = React.useState(() => {
        try { return sessionStorage.getItem(CHAT_OPENED_KEY) === "1" } catch (_) { return false }
    })

    // Restaure une session précédente (même onglet uniquement, sessionStorage)
    React.useEffect(() => {
        const saved = readSessionFromStorage()
        if (saved && saved.siteIdentifier === siteIdentifier && Array.isArray(saved.messages)) {
            setSessionToken(saved.sessionToken || null)
            setMessages(saved.messages)
        }
    }, [siteIdentifier])

    // Persist sessionStorage à chaque changement
    React.useEffect(() => {
        if (!sessionToken && messages.length === 0) return
        writeSessionToStorage({siteIdentifier, sessionToken, messages})
    }, [sessionToken, messages, siteIdentifier])

    // Teaser d'incitation. Stratégie :
    //   - première apparition rapide après l'arrivée sur la page,
    //   - réapparition périodique tant que l'utilisateur n'a pas ouvert le
    //     chat ET tant qu'il n'a pas explicitement fermé le teaser,
    //   - rotation des messages pour varier le visuel.
    // Une fois le chat ouvert OU le teaser fermé manuellement, on stoppe.
    React.useEffect(() => {
        if (!enabled || !siteIdentifier || teaserMessages.length === 0) return
        if (openedOnce) return
        let dismissed = false
        try {
            dismissed = sessionStorage.getItem(TEASER_DISMISSED_KEY) === "1"
        } catch (_) {
        }
        if (dismissed) return

        let cancelled = false
        const timers = []

        function scheduleCycle(delay) {
            const showTimer = setTimeout(() => {
                if (cancelled) return
                setTeaserIndex(i => (i + 1) % teaserMessages.length)
                setTeaserVisible(true)
                const hideTimer = setTimeout(() => {
                    if (cancelled) return
                    setTeaserVisible(false)
                    scheduleCycle(TEASER_REPEAT_INTERVAL_MS)
                }, TEASER_VISIBLE_MS)
                timers.push(hideTimer)
            }, delay)
            timers.push(showTimer)
        }

        scheduleCycle(TEASER_FIRST_DELAY_MS)

        return () => {
            cancelled = true
            timers.forEach(clearTimeout)
            setTeaserVisible(false)
        }
    }, [enabled, siteIdentifier, openedOnce, teaserMessages.length])

    // Fermer le teaser explicitement = on n'y revient plus de la session.
    function dismissTeaser() {
        setTeaserVisible(false)
        try {
            sessionStorage.setItem(TEASER_DISMISSED_KEY, "1")
        } catch (_) {
        }
    }

    // Lock du scroll du body quand le panneau plein écran est ouvert (mobile).
    // Sans ça, dès que l'utilisateur tape dans le textarea ou scrolle dans la
    // liste de messages, c'est tout le body de la page qui se met à bouger
    // derrière, donnant l'impression que "ça scrolle dans tous les sens".
    React.useEffect(() => {
        if (!isMobile) return
        if (!open) return
        const body = document.body
        const html = document.documentElement
        const prevBodyOverflow = body.style.overflow
        const prevHtmlOverflow = html.style.overflow
        body.style.overflow = "hidden"
        html.style.overflow = "hidden"
        return () => {
            body.style.overflow = prevBodyOverflow
            html.style.overflow = prevHtmlOverflow
        }
    }, [isMobile, open])

    // Beacon de fin de session quand l'utilisateur quitte la page
    React.useEffect(() => {
        if (!sessionToken) return
        const beacon = () => {
            try {
                const blob = new Blob([JSON.stringify({sessionToken})], {type: "application/json"})
                navigator.sendBeacon(ENDPOINT + "/end", blob)
            } catch (_) {
            }
        }
        window.addEventListener("pagehide", beacon)
        return () => window.removeEventListener("pagehide", beacon)
    }, [sessionToken])

    async function ensureSession() {
        if (sessionToken) return sessionToken
        if (!siteIdentifier) throw new Error("MISSING_SITE")

        const res = await fetch(ENDPOINT + "/session", {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({siteIdentifier, channel: "web", language}),
        })
        const data = await res.json()
        if (!data.success) throw new Error(data.error || "SESSION_FAILED")

        setSessionToken(data.sessionToken)
        return data.sessionToken
    }

    // Consomme le stream SSE depuis /message-stream. Le serveur pipe
    // directement les frames ADK natives, donc chaque event ressemble à :
    //   { content: { parts: [{ text: "..." }] }, partial: true|false, ... }
    // On accumule les `partial:true` (chunks token-level) et on se cale sur
    // `partial:false` comme texte définitif (final ADK). Aligné sur le
    // pattern de IAChat dans l'admin.
    async function streamFromServer(token, text) {
        const res = await fetch(ENDPOINT + "/message-stream", {
            method: "POST",
            headers: {"Content-Type": "application/json", "Accept": "text/event-stream"},
            body: JSON.stringify({sessionToken: token, message: text}),
        })

        if (!res.ok) {
            try {
                const j = await res.json()
                return j?.error || "HTTP_" + res.status
            } catch (_) {
                return "HTTP_" + res.status
            }
        }
        if (!res.body || !res.body.getReader) return "NO_STREAM_SUPPORT"

        // Bulle assistant qu'on remplira progressivement. Id unique pour
        // tolérer les ré-exécutions du reducer (StrictMode). On démarre en
        // état `thinking` pour que l'utilisateur voie un placeholder pendant
        // les éventuels tool-calls qui précèdent le 1er token texte.
        const bubbleId = "asst_" + Date.now() + "_" + Math.random().toString(36).slice(2, 8)
        setMessages(prev => [...prev, {id: bubbleId, role: "assistant", text: "", thinking: true}])
        // On peut cacher le typing indicator global puisque la bulle elle-même
        // affiche maintenant l'état "réflexion".
        setPending(false)

        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buf = ""
        let accumulated = ""
        let finalText = ""
        let gotAnyText = false

        const applyText = (newFullText) => {
            gotAnyText = true
            setMessages(prev => prev.map(m =>
                m.id === bubbleId ? {...m, text: newFullText, thinking: false} : m,
            ))
        }

        while (true) {
            const {value, done} = await reader.read()
            if (done) break
            buf += decoder.decode(value, {stream: true})

            let idx
            while ((idx = buf.indexOf("\n\n")) !== -1) {
                const frame = buf.slice(0, idx)
                buf = buf.slice(idx + 2)
                const ev = parseAdkSseFrame(frame)
                if (!ev) continue

                const parts = ev?.content?.parts || []
                const texts = parts
                    .filter(p => typeof p.text === "string")
                    .map(p => p.text)

                // Pas de texte dans cet event = c'est probablement un
                // function_call ou function_response (étape de réflexion
                // de l'agent qui appelle un tool). On laisse la bulle en
                // état "thinking" jusqu'au prochain event texte.
                if (!texts.length) continue

                const joined = texts.join("")

                if (ev.partial === true) {
                    accumulated += joined
                    applyText(accumulated)
                } else if (ev.partial === false) {
                    finalText = joined
                    accumulated = joined
                    applyText(joined)
                } else {
                    finalText = joined
                    if (joined.length >= accumulated.length) {
                        accumulated = joined
                        applyText(joined)
                    }
                }
            }
        }

        if (!gotAnyText) {
            // Stream fini sans aucun event texte (rare). On retire la bulle.
            setMessages(prev => prev.filter(m => m.id !== bubbleId))
            return "EMPTY_STREAM"
        }
        return null
    }

    async function send(text) {
        setError(null)
        const userMsg = {role: "user", text}
        setMessages(prev => [...prev, userMsg])
        setPending(true)

        try {
            const token = await ensureSession()
            let err = await streamFromServer(token, text)

            // Session expirée : on en redémarre une et on retente une fois.
            if (err === "SESSION_EXPIRED") {
                clearSessionStorage()
                setSessionToken(null)
                const newToken = await ensureSession()
                err = await streamFromServer(newToken, text)
            }

            if (err) throw new Error(err)
        } catch (e) {
            console.warn("[trchat] send failed", e?.message || e)
            setError(t`Une erreur est survenue. Réessayez dans un instant.`)
        } finally {
            setPending(false)
        }
    }

    function openChat() {
        setOpen(true)
        setClosing(false)
        dismissTeaser()
        if (!openedOnce) {
            setOpenedOnce(true)
            try { sessionStorage.setItem(CHAT_OPENED_KEY, "1") } catch (_) {}
        }
        // Premier ouverture : injecte le message d'accueil
        if (messages.length === 0 && welcomeMessage) {
            setMessages([{role: "assistant", text: welcomeMessage}])
        }
    }

    function closeChat() {
        setClosing(true)
        setTimeout(() => {
            setOpen(false)
            setClosing(false)
        }, 220)
    }

    // Reset complet : on dit au serveur que la session est terminée (pour ne
    // pas la laisser traîner côté ADK), on vide l'état local, et on réinjecte
    // le message d'accueil. La prochaine question créera une nouvelle session.
    async function resetChat() {
        const tokenToEnd = sessionToken
        setSessionToken(null)
        setMessages(welcomeMessage ? [{role: "assistant", text: welcomeMessage}] : [])
        setError(null)
        clearSessionStorage()
        if (tokenToEnd) {
            try {
                await fetch(ENDPOINT + "/end", {
                    method: "POST",
                    headers: {"Content-Type": "application/json"},
                    body: JSON.stringify({sessionToken: tokenToEnd}),
                })
            } catch (_) {
            }
        }
    }

    // En mode embed (iframe intégrée), on masque le chatbot : il prendrait
    // une place disproportionnée dans un widget intégré et le contexte
    // d'usage n'est pas adapté.
    if (context?.render === "embed") return null

    if (!enabled || !siteIdentifier) return null

    const headerLabel = context?.org?.name || context?.org?.shortName || t`Assistant`

    // Wrapper racine du widget : on y attache toutes les CSS variables dérivées
    // de --trchat-primary. Comme c'est une div ancêtre commune au FAB et au
    // panneau, les deux la voient via la cascade — pas besoin de répéter
    // l'injection. color-mix() est supporté partout sauf très vieux Safari, et
    // dégrade vers le fallback proprement.
    const widgetVars = {
        "--trchat-primary": primaryColor,
    }

    const showTeaser = teaserVisible && teaserMessages.length > 0 && !open && !closing
    // Pulse continu et badge "•" tant que le chat n'a jamais été ouvert sur
    // cet onglet. Une fois ouvert, le FAB redevient neutre.
    const showPulse = !openedOnce && !open
    const showBadge = !openedOnce && !open
    const teaser = teaserMessages[teaserIndex % teaserMessages.length]

    return (
        <div className="trchat-root" dir={language === "he" ? "rtl" : "ltr"} style={widgetVars}>
            {showTeaser && (
                <div
                    key={teaserIndex}
                    className="trchat__teaser"
                >
                    <button
                        type="button"
                        className="trchat__teaser-close"
                        aria-label={t`Fermer`}
                        onClick={(e) => { e.stopPropagation(); dismissTeaser() }}
                    >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                    <button type="button" className="trchat__teaser-text" onClick={openChat}>
                        <span className="trchat__teaser-emoji" aria-hidden="true">{teaser.emoji}</span>
                        <span>{teaser.text}</span>
                    </button>
                </div>
            )}
            <button
                type="button"
                className={[
                    "trchat__fab",
                    open ? "trchat__fab--hidden" : "",
                    showPulse ? "trchat__fab--pulse" : "",
                ].filter(Boolean).join(" ")}
                onClick={openChat}
                aria-label={t`Ouvrir le chat`}
            >
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                {showBadge && <span className="trchat__fab-badge" aria-hidden="true"/>}
            </button>

            <ChatPanel
                open={open}
                closing={closing}
                isMobile={isMobile}
                messages={messages}
                pending={pending}
                error={error}
                onSend={send}
                onClose={closeChat}
                onReset={resetChat}
                headerLabel={headerLabel}
            />

            <style dangerouslySetInnerHTML={{__html: STYLES}}/>
        </div>
    )
}


// Couleurs : on s'appuie sur la primary color de l'org (passée en style inline
// sur .trchat-root via la prop) et on en dérive des nuances via color-mix.
// Pas de gradient — ton uni partout, comme les composants InfoTiles du thème
// Travel (mélange avec blanc pour les fonds très doux, avec noir pour le hover).

const STYLES = `
.trchat-root {
    --trchat-primary-tint:   color-mix(in srgb, var(--trchat-primary) 8%,  white);
    --trchat-primary-soft:   color-mix(in srgb, var(--trchat-primary) 14%, white);
    --trchat-primary-hover:  color-mix(in srgb, var(--trchat-primary) 88%, black);
    --trchat-primary-shadow: color-mix(in srgb, var(--trchat-primary) 35%, transparent);
}

/* FAB d'ouverture --------------------------------------------------------- */

.trchat__fab {
    position: fixed;
    inset-inline-end: 20px;
    bottom: 20px;
    width: 58px;
    height: 58px;
    border-radius: 50%;
    border: none;
    background: var(--trchat-primary);
    color: #fff;
    cursor: pointer;
    box-shadow:
        0 8px 22px var(--trchat-primary-shadow),
        0 2px 6px rgba(0, 0, 0, .08);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9998;
    transition: transform .2s cubic-bezier(.34, 1.56, .64, 1), background .15s ease, box-shadow .2s ease, opacity .2s ease;
}

.trchat__fab:hover {
    background: var(--trchat-primary-hover);
    transform: translateY(-2px);
    box-shadow:
        0 12px 28px var(--trchat-primary-shadow),
        0 4px 10px rgba(0, 0, 0, .1);
}

.trchat__fab:active { transform: translateY(0); }

.trchat__fab--hidden {
    opacity: 0;
    pointer-events: none;
    transform: scale(.8) translateY(8px);
}

/* Pulse continu + wiggle périodique tant que le chat n'a jamais été ouvert.
   Deux animations parallèles : un halo qui respire (attire l'œil de loin)
   et un petit wiggle toutes les ~6s (attire l'œil vers le FAB lui-même). */
.trchat__fab--pulse {
    animation:
        trchat-fab-pulse 2.4s ease-out infinite,
        trchat-fab-wiggle 6s ease-in-out infinite;
}

@keyframes trchat-fab-pulse {
    0%   { box-shadow: 0 0 0 0 var(--trchat-primary-shadow), 0 8px 22px var(--trchat-primary-shadow), 0 2px 6px rgba(0, 0, 0, .08); }
    70%  { box-shadow: 0 0 0 18px transparent,            0 8px 22px var(--trchat-primary-shadow), 0 2px 6px rgba(0, 0, 0, .08); }
    100% { box-shadow: 0 0 0 0 transparent,               0 8px 22px var(--trchat-primary-shadow), 0 2px 6px rgba(0, 0, 0, .08); }
}

@keyframes trchat-fab-wiggle {
    0%, 80%, 100% { transform: rotate(0); }
    82%           { transform: rotate(-8deg); }
    85%           { transform: rotate(7deg); }
    88%           { transform: rotate(-5deg); }
    91%           { transform: rotate(3deg); }
    94%           { transform: rotate(0); }
}

/* Le wiggle ne doit pas s'appliquer au hover/active (sinon le rotate
   écrase le translateY). On désactive juste l'animation au survol. */
.trchat__fab--pulse:hover,
.trchat__fab--pulse:active {
    animation: trchat-fab-pulse 2.4s ease-out infinite;
}

/* Badge de notification rouge sur le FAB tant que jamais ouvert. */
.trchat__fab-badge {
    position: absolute;
    top: 4px;
    inset-inline-end: 4px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #ef4444;
    border: 2px solid #fff;
    box-shadow: 0 0 0 0 rgba(239, 68, 68, .55);
    animation: trchat-badge-pulse 1.6s ease-out infinite;
    pointer-events: none;
}

@keyframes trchat-badge-pulse {
    0%   { box-shadow: 0 0 0 0 rgba(239, 68, 68, .55); }
    70%  { box-shadow: 0 0 0 7px rgba(239, 68, 68, 0); }
    100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
}

/* Teaser d'incitation ----------------------------------------------------- */

.trchat__teaser {
    position: fixed;
    inset-inline-end: 90px;
    bottom: 28px;
    max-width: 260px;
    padding-block: 10px;
    padding-inline: 14px 32px;
    background: #fff;
    color: #1a1a1a;
    border-radius: 14px;
    box-shadow:
        0 10px 28px rgba(0, 0, 0, .14),
        0 2px 6px rgba(0, 0, 0, .06),
        0 0 0 1px rgba(0, 0, 0, .04);
    font-size: 13.5px;
    line-height: 1.35;
    cursor: pointer;
    z-index: 9997;
    animation: trchat-teaser-in .35s cubic-bezier(.16, 1, .3, 1) both, trchat-teaser-bob 3s 1.6s ease-in-out infinite;
    transform-origin: bottom right;
}

.trchat__teaser::after {
    content: "";
    position: absolute;
    inset-inline-end: -7px;
    bottom: 18px;
    width: 12px;
    height: 12px;
    background: #fff;
    transform: rotate(45deg);
    box-shadow: 1px -1px 0 0 rgba(0, 0, 0, .04);
}

.trchat__teaser:hover {
    transform: translateY(-1px);
    box-shadow:
        0 14px 32px rgba(0, 0, 0, .18),
        0 3px 8px rgba(0, 0, 0, .08),
        0 0 0 1px rgba(0, 0, 0, .06);
}

.trchat__teaser-text {
    width: 100%;
    border: 0;
    padding: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: start;
    cursor: pointer;
    min-width: 0;
    max-height: calc(100dvh - 160px);
    overflow-y: auto;
    overflow-wrap: anywhere;
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 500;
}

.trchat__teaser-emoji {
    font-size: 18px;
    display: inline-block;
    animation: trchat-teaser-wave 1.4s ease-in-out 2;
    transform-origin: 70% 70%;
}

.trchat__teaser-close {
    position: absolute;
    top: 6px;
    inset-inline-end: 6px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: none;
    background: rgba(0, 0, 0, .06);
    color: rgba(0, 0, 0, .55);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background .15s ease;
    padding: 0;
}
.trchat__teaser-text:focus-visible, .trchat__teaser-close:focus-visible { outline: 2px solid var(--trchat-primary); outline-offset: 2px; }

.trchat__teaser-close:hover { background: rgba(0, 0, 0, .12); color: rgba(0, 0, 0, .8); }

@keyframes trchat-teaser-in {
    from { opacity: 0; transform: translateY(8px) scale(.92); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes trchat-teaser-bob {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-4px); }
}

@keyframes trchat-teaser-wave {
    0%, 100% { transform: rotate(0); }
    20%      { transform: rotate(-14deg); }
    40%      { transform: rotate(12deg); }
    60%      { transform: rotate(-8deg); }
    80%      { transform: rotate(6deg); }
}

[dir="rtl"] .trchat__teaser {
    transform-origin: bottom left;
}

/* Panneau ----------------------------------------------------------------- */

.trchat__panel {
    position: fixed;
    inset-inline-end: 20px;
    bottom: 20px;
    width: 380px;
    height: min(640px, calc(100vh - 40px));
    background: #fff;
    border-radius: 16px;
    box-shadow:
        0 20px 60px rgba(0, 0, 0, .16),
        0 4px 14px rgba(0, 0, 0, .06),
        0 0 0 1px rgba(0, 0, 0, .04);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    z-index: 9999;
    animation: trchat-in .24s cubic-bezier(.16, 1, .3, 1) both;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    color: #1a1a1a;
}

.trchat__panel--closing {
    animation: trchat-out .2s cubic-bezier(.4, 0, 1, 1) both;
}

.trchat__panel--mobile {
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    /* dvh = dynamic viewport height : suit la barre d'URL Safari/Chrome qui
       apparaît/disparaît au scroll. Sans ça, le panneau dépasse l'écran et
       le clavier pousse l'input hors viewport. Fallback en vh pour les
       très vieux navigateurs. */
    height: 100vh;
    height: 100dvh;
    border-radius: 0;
    /* Padding safe-area pour les iPhones avec notch / home bar. */
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
    box-sizing: border-box;
}

@keyframes trchat-in {
    from { opacity: 0; transform: translateY(16px) scale(.96); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes trchat-out {
    from { opacity: 1; transform: translateY(0) scale(1); }
    to   { opacity: 0; transform: translateY(8px) scale(.98); }
}

/* Header ------------------------------------------------------------------ */

.trchat__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    background: var(--trchat-primary);
    color: #fff;
    flex-shrink: 0;
}

.trchat__header-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.trchat__header-title {
    font-size: 15px;
    font-weight: 600;
    line-height: 1.2;
    letter-spacing: -.01em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.trchat__header-status {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    opacity: .85;
}

.trchat__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #4ade80;
    box-shadow: 0 0 0 0 rgba(74, 222, 128, .6);
    animation: trchat-pulse 2s infinite;
}

@keyframes trchat-pulse {
    0%   { box-shadow: 0 0 0 0 rgba(74, 222, 128, .6); }
    70%  { box-shadow: 0 0 0 6px rgba(74, 222, 128, 0); }
    100% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0); }
}

.trchat__header-actions {
    display: flex;
    align-items: center;
    gap: 6px;
}

.trchat__icon-btn {
    background: rgba(255, 255, 255, .12);
    border: none;
    color: #fff;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background .15s ease, opacity .15s ease;
}

.trchat__icon-btn:hover:not(:disabled) { background: rgba(255, 255, 255, .22); }
.trchat__icon-btn:disabled { opacity: .4; cursor: not-allowed; }

/* Messages ---------------------------------------------------------------- */

.trchat__messages {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: var(--trchat-primary-tint);
    -webkit-overflow-scrolling: touch;
    /* Empêche le scroll de "déborder" sur la page (rebond Safari, propagation
       du scroll au body derrière le panneau). */
    overscroll-behavior: contain;
}

.trchat__messages::-webkit-scrollbar { width: 6px; }
.trchat__messages::-webkit-scrollbar-thumb { background: rgba(0, 0, 0, .15); border-radius: 3px; }

/* Note système (disclaimer en haut de la conversation) ------------------- */

.trchat__system-note {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
    margin: -4px -4px 8px;
    background: rgba(0, 0, 0, .035);
    border: 1px solid rgba(0, 0, 0, .06);
    border-radius: 10px;
    font-size: 11.5px;
    line-height: 1.4;
    color: #6b7280;
    font-style: italic;
}

.trchat__system-note svg {
    flex-shrink: 0;
    margin-top: 1px;
    color: #9ca3af;
}

.trchat__msg {
    display: flex;
    max-width: 86%;
}

.trchat__msg--user      { align-self: flex-end; justify-content: flex-end; }
.trchat__msg--assistant { align-self: flex-start; }

.trchat__bubble {
    padding: 10px 14px;
    border-radius: 16px;
    font-size: 14.5px;
    line-height: 1.5;
    word-break: break-word;
    white-space: pre-wrap;
}

.trchat__msg--user .trchat__bubble {
    background: var(--trchat-primary);
    color: #fff;
    border-bottom-right-radius: 4px;
}

.trchat__msg--assistant .trchat__bubble {
    background: #fff;
    color: #1a1a1a;
    border: 1px solid rgba(0, 0, 0, .06);
    border-bottom-left-radius: 4px;
}

/* Markdown — bulle assistant ---------------------------------------------- */

.trchat__bubble--md { white-space: normal; }
.trchat__bubble--md > *:first-child { margin-top: 0; }
.trchat__bubble--md > *:last-child  { margin-bottom: 0; }
.trchat__bubble--md p                { margin: 0 0 6px; }
.trchat__bubble--md p:last-child     { margin-bottom: 0; }
.trchat__bubble--md strong           { font-weight: 600; color: #0d0d0d; }
.trchat__bubble--md em               { font-style: italic; }
.trchat__bubble--md ul,
.trchat__bubble--md ol               { margin: 4px 0 6px; padding-left: 20px; }
.trchat__bubble--md li               { margin: 2px 0; }
.trchat__bubble--md a {
    color: var(--trchat-primary);
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 2px;
    word-break: break-word;
}
.trchat__bubble--md a:hover { color: var(--trchat-primary-hover); }
.trchat__bubble--md code {
    background: rgba(0, 0, 0, .06);
    padding: 1px 6px;
    border-radius: 4px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 13px;
}
.trchat__bubble--md pre {
    background: #f3f4f6;
    border: 1px solid rgba(0, 0, 0, .06);
    border-radius: 8px;
    padding: 10px 12px;
    overflow-x: auto;
    margin: 6px 0;
}
.trchat__bubble--md pre code {
    background: transparent;
    padding: 0;
    font-size: 12.5px;
    color: #1a1a1a;
}
.trchat__bubble--md blockquote {
    margin: 6px 0;
    padding: 4px 10px;
    border-left: 3px solid var(--trchat-primary);
    color: #444;
    background: var(--trchat-primary-tint);
    border-radius: 0 6px 6px 0;
}

/* Réflexion (tool-calls / délai avant 1er token) -------------------------- */

.trchat__bubble--thinking {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    background: #fff;
    color: #6b7280;
    border: 1px solid rgba(0, 0, 0, .06);
    border-radius: 16px;
    border-bottom-left-radius: 4px;
    font-size: 13.5px;
    font-style: italic;
}

.trchat__thinking-dots {
    display: inline-flex;
    gap: 3px;
}

.trchat__thinking-dots span {
    width: 5px;
    height: 5px;
    background: var(--trchat-primary);
    border-radius: 50%;
    opacity: .55;
    animation: trchat-bounce 1.2s infinite ease-in-out;
}

.trchat__thinking-dots span:nth-child(2) { animation-delay: .15s; }
.trchat__thinking-dots span:nth-child(3) { animation-delay: .3s; }

.trchat__thinking-label {
    line-height: 1.2;
}

/* Typing ------------------------------------------------------------------ */

.trchat__bubble--typing {
    display: inline-flex;
    gap: 4px;
    padding: 14px 16px;
}

.trchat__bubble--typing span {
    width: 6px;
    height: 6px;
    background: var(--trchat-primary);
    border-radius: 50%;
    animation: trchat-bounce 1.2s infinite ease-in-out;
    opacity: .55;
}

.trchat__bubble--typing span:nth-child(2) { animation-delay: .15s; }
.trchat__bubble--typing span:nth-child(3) { animation-delay: .3s; }

@keyframes trchat-bounce {
    0%, 60%, 100% { transform: translateY(0);  opacity: .4; }
    30%           { transform: translateY(-4px); opacity: 1; }
}

/* Erreur ------------------------------------------------------------------ */

.trchat__error {
    padding: 8px 16px;
    font-size: 12.5px;
    color: #b42318;
    background: #fef3f2;
    border-top: 1px solid #fecdca;
    text-align: center;
}

/* Input ------------------------------------------------------------------- */

.trchat__input-row {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    padding: 10px 12px;
    border-top: 1px solid rgba(0, 0, 0, .06);
    background: #fff;
    flex-shrink: 0;
}

.trchat__input {
    flex: 1;
    border: 1px solid rgba(0, 0, 0, .1);
    border-radius: 12px;
    padding: 10px 14px;
    font-size: 14.5px;
    font-family: inherit;
    resize: none;
    outline: none;
    line-height: 1.4;
    max-height: 120px;
    background: #fafafa;
    color: #1a1a1a;
    transition: border-color .15s ease, background .15s ease, box-shadow .15s ease;
}

.trchat__input:focus {
    border-color: var(--trchat-primary);
    background: #fff;
    box-shadow: 0 0 0 3px var(--trchat-primary-soft);
}

.trchat__input:disabled { opacity: .6; cursor: not-allowed; }

.trchat__send {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: none;
    background: var(--trchat-primary);
    color: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background .15s ease, transform .14s ease, opacity .15s ease;
}

.trchat__send:hover:not(:disabled) {
    background: var(--trchat-primary-hover);
    transform: scale(1.04);
}

.trchat__send:disabled { opacity: .4; cursor: not-allowed; }

@media (prefers-reduced-motion: reduce) {
    .trchat__fab--pulse, .trchat__fab-badge, .trchat__teaser, .trchat__teaser-emoji { animation: none; }
}

@media (max-width: 768px) {
    .trchat__fab    { inset-inline-end: 16px; bottom: 16px; width: 54px; height: 54px; }
    .trchat__panel  { border-radius: 0; }
    .trchat__teaser { inset-inline-end: 80px; bottom: 22px; max-width: 220px; font-size: 13px; }

    /* iOS Safari zoome auto sur tout input/textarea avec font-size < 16px.
       On force 16px sur mobile pour empêcher ce comportement très désagréable
       (le viewport zoome au focus et ne dézoome jamais tout seul). */
    .trchat__panel--mobile .trchat__input {
        font-size: 16px;
        line-height: 1.4;
        padding: 12px 14px;
    }

    /* Cible de touch plus grande pour le bouton envoyer. */
    .trchat__panel--mobile .trchat__send {
        width: 44px;
        height: 44px;
    }

    /* Le header peut être un peu plus compact. */
    .trchat__panel--mobile .trchat__header {
        padding: 12px 14px;
    }

    /* Sur mobile on a tendance à vouloir une zone de saisie un peu plus
       confortable au-dessus du clavier. */
    .trchat__panel--mobile .trchat__input-row {
        padding: 10px 12px 12px;
        gap: 10px;
    }

    /* Bulles : un peu plus large, font-size confortable au pouce. */
    .trchat__panel--mobile .trchat__bubble {
        font-size: 15px;
    }

    /* Évite que le bubble du markdown soit "écrasé" par le clavier. */
    .trchat__panel--mobile .trchat__messages {
        padding: 14px 12px;
    }
}
`
